import React, { useEffect, useState } from 'react';
import { tokens } from '@domain-group/fe-brary';
import { Badge } from '@domain-group/fe-blueprint-ui-x/badge';
import { StarRating } from '../shared/star-rating';
import {
  formatReviewDate,
  formatReviewDateAbsolute,
} from '../../utils/format-review-date';
import TruncatedReviewText from './truncated-review-text';
import * as styles from './review-card.style';

export type ReviewCardProps = {
  title: string;
  description: string;
  reviewedOn: string;
  starRating: number;
  reviewType?: string | null;
  bedrooms?: number | null;
  propertyType?: string | null;
  suburb?: string | null;
};

const ReviewCard = ({
  title,
  description,
  reviewedOn,
  starRating,
  reviewType,
  bedrooms,
  propertyType,
  suburb,
}: ReviewCardProps): JSX.Element => {
  const isSellerReview = reviewType === 'vendor';
  const propertyInfo =
    bedrooms && propertyType && suburb
      ? `${bedrooms} bedroom ${propertyType.toLowerCase()} in ${suburb}`
      : null;

  // The relative label (e.g. "Today", "3 days ago") depends on the
  // renderer's clock and time zone, which can differ between the server
  // and the client. Render a deterministic absolute date first, then swap
  // in the relative label once mounted on the client to avoid a hydration
  // mismatch.
  const [relativeDate, setRelativeDate] = useState<string | null>(null);
  useEffect(() => {
    setRelativeDate(formatReviewDate(reviewedOn));
  }, [reviewedOn]);

  return (
    <article css={styles.card} data-testid="agent-profile-review-card">
      <div css={styles.top}>
        <div css={styles.ratingAndBadgeRow}>
          <div css={styles.ratingAndTimestamp}>
            <span
              css={styles.stars}
              role="img"
              aria-label={`${starRating} out of 5 stars`}
            >
              <StarRating
                rating={starRating}
                starSize={14}
                fillStar={tokens.colors.accentFour.trim.hover}
              />
            </span>
            <span css={styles.dotSeparator}>·</span>
            <span css={styles.relativeDate}>
              {relativeDate ?? formatReviewDateAbsolute(reviewedOn)}
            </span>
          </div>
          {isSellerReview && <Badge family="accentThree">Seller review</Badge>}
        </div>
        {propertyInfo && <p css={styles.propertyInfo}>{propertyInfo}</p>}
      </div>
      <div css={styles.reviewCopy}>
        <h3 css={styles.title}>{title}</h3>
        <TruncatedReviewText description={description} />
      </div>
    </article>
  );
};

export default ReviewCard;
